import React from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingNav from '../components/layout/MarketingNav';
import MarketingFooter from '../components/layout/MarketingFooter';

/**
 * HowItWorksPage — Mental Health Focused Rewrite
 * Explains the daily program structure, both pathways, and what to expect.
 * Mental wellness includes emotional, mental, and physical wellbeing. Not addiction-specific.
 */

const BRAND = {
  forest: '#2D5A47', forestDark: '#1E3D30', forestLight: '#4A7C67',
    detail: 'Miss a day? No shame, no reset. You continue from where you left off. Mental wellness growth isn\'t about perfection.',
  blue: '#7BA3B8', blueLight: '#EDF4F8',
  dark: '#1A1A2E', text: '#2C2C2C', muted: '#6B6B7B',
  light: '#F8F6F3', white: '#FFFFFF',
};

const steps = [
  {
    step: '01',
    title: 'Choose your pathway.',
    description: 'Start on your own with Self-Guided, or enroll with a clinician referral code for the Clinical pathway. Both deliver the exact same 365-day program. The difference is whether your provider monitors your engagement with your consent.',
    detail: 'You can switch pathways or grant/revoke clinician access at any time from your profile settings.',
    color: BRAND.blue,
  },
  {
    step: '02',
    title: 'Complete your daily practice. 15–45 minutes.',
    description: 'Each day presents three structured core practices — one for your Mind, one for your body (Move), and one for your inner life (Journal). The optional Faith pathway remains available throughout the full 365 days for those who want it.',
    detail: 'Miss a day? No shame, no reset. You continue from where you left off. Mental wellness growth isn\'t about perfection.',
    color: BRAND.forest,
  },
  {
    step: '03',
    title: 'Track your patterns over time.',
    description: 'Your dashboard shows mood trends, challenge intensity, streak data, and completion rates across all four phases. You\'ll start to see what works, what doesn\'t, and how your emotional landscape shifts.',
    detail: 'All journal entries are encrypted end-to-end. Your provider can only access what you explicitly authorize.',
    color: BRAND.sand,
  },
  {
    step: '04',
    title: 'Build your Personal Wellness Playbook.',
    description: 'In Phase 4 (Days 271–365), you build a personalized guide to maintaining your mental health long after Day 365. Not a list of rules — a living document of what actually works for you.',
    detail: 'This becomes yours to keep. The practices you build over 365 days don\'t disappear when the program ends.',
    color: BRAND.forestDark,
  },
];

const pathways = [
  {
    label: 'Self-Guided',
    tagline: 'Full autonomy. Complete privacy.',
    icon: '🧭',
    color: BRAND.blue,
    lightColor: BRAND.blueLight,
    description: 'Access the complete 365-day program independently. No referral needed, no provider involvement unless you want it. Full control over your data, your pace, and your experience.',
    includes: [
      'Complete 365-day Mind, Move, Journal program',
      'Encrypted journaling with mood & wellness tracking',
      'Progress dashboard and trend visualization',
      'Personal Wellness Playbook (Phase 4)',
      'Optional Biblical Foundations Track',
      'Full data export capability',
    ],
    forWho: 'For anyone navigating anxiety, depression, grief, trauma, stress, or any form of emotional or behavioral healing — independently.',
  },
  {
    label: 'Clinical Pathway',
    tagline: 'Therapist-integrated. Consent-first.',
    icon: '🩺',
    color: BRAND.forest,
    lightColor: '#E8F7F3',
    description: 'Enroll via your therapist\'s referral code and integrate Momentum directly into your treatment plan. Your clinician gets a dashboard showing your engagement and trends — only what you consent to share.',
    includes: [
      'Everything in Self-Guided, plus:',
      'Clinician dashboard with consent-gated access',
      'Engagement data visible to your provider',
      'Clinical integration into treatment plan',
      'Template-based encouragement from your clinician',
      'Session-ready progress summaries',
    ],
    forWho: 'For clients already working with a licensed therapist who wants to extend the therapeutic work into daily practice.',
  },
];

const dailyBreakdown = [
  {
    pillar: 'Mind',
    icon: '🧠',
    time: '10–20 min',
    color: BRAND.forest,
    what: 'A structured cognitive or emotional exercise drawn from CBT, DBT, or ACT — the three most evidence-supported frameworks in mental health treatment.',
    examples: ['Thought record worksheet', 'DBT TIPP skill practice', 'ACT defusion exercise', 'Cognitive restructuring prompt', 'Values clarification activity'],
  },
  {
    pillar: 'Move',
    icon: '⚡',
    time: '15–45 min',
    color: BRAND.blue,
    what: 'A specific physical activity with direct neurological and emotional benefits. Not generic exercise — deliberately chosen for its impact on the stress response, mood regulation, and nervous system regulation.',
    examples: ['Vagal nerve breathing walk', 'Cold exposure (beginner)', 'Weighted movement for grounding', 'Aerobic activity for dopamine', 'Yoga for nervous system reset'],
  },
  {
    pillar: 'Journal',
    icon: '✍️',
    time: '10–15 min',
    color: BRAND.sand,
    what: 'A guided reflection prompt tied to that day\'s theme, your current phase, and your tracked mood data. Encrypted. Private unless you choose to share.',
    examples: ['Processing a difficult emotion', 'Tracking a difficult thought or urge', 'Gratitude and growth reflection', 'Identifying today\'s trigger', 'Progress acknowledgment'],
  },
];

const HowItWorksPage = () => {
  const navigate = useNavigate();

  return (
    <div className="site-canvas" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', color: BRAND.text, minHeight: '100vh' }}>
      <MarketingNav transparent={true} />

      {/* ── HERO ── */}
      <section className="marketing-photo-hero" style={{ backgroundImage: "url('/Window Moment.png')" }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '1280px', paddingTop: '130px', paddingBottom: '110px' }}>
          <div className="marketing-photo-hero-panel">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,209,102,0.15)', border: '1px solid rgba(255,209,102,0.3)', borderRadius: '100px', padding: '7px 18px', marginBottom: '24px' }}>
            <span style={{ color: '#ffd166', fontSize: '0.8rem', fontWeight: '600', letterSpacing: '0.07em', textTransform: 'uppercase' }}>How It Works</span>
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', fontWeight: '800', color: '#f1fff8', lineHeight: '1.1', letterSpacing: '-0.025em', marginBottom: '20px' }}>
            15 minutes a day.
            <br /><span style={{ color: '#ffd166' }}>365 days of real change.</span>
          </h1>
          <p style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)', color: 'rgba(255,255,255,0.72)', lineHeight: '1.7', maxWidth: '540px', margin: '0 0 36px 0' }}>
            Not a productivity app. Not a mood tracker. A clinically structured daily program for
            anyone building emotional regulation, mental stability, and lasting wellbeing —
            with or without a therapist.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')}
              style={{ background: '#ffd166', color: '#041b12', fontWeight: '700', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f2c14e'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffd166'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Start My Daily Practice
            </button>
            <button onClick={() => navigate('/preview/client')}
              style={{ background: 'transparent', color: BRAND.white, fontWeight: '600', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.65)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'transparent'; }}>
              Preview the Experience
            </button>
          </div>
          </div>
        </div>
      </section>

      {/* ── STEP-BY-STEP ── */}
      <section style={{ background: BRAND.white, padding: 'clamp(64px, 10vw, 120px) 24px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <h2 style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)', fontWeight: '800', color: BRAND.dark, letterSpacing: '-0.02em', marginBottom: '12px' }}>
              How the program works
            </h2>
            <p style={{ fontSize: '1rem', color: BRAND.muted, lineHeight: '1.65', maxWidth: '480px', margin: '0 auto' }}>
              Four steps from Day 1 to a life that doesn't require constant management.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {steps.map((step, i) => (
              <div key={step.step} style={{ display: 'flex', gap: '32px', position: 'relative', paddingBottom: i < steps.length - 1 ? '48px' : '0', alignItems: 'flex-start' }}>
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div style={{ position: 'absolute', left: '21px', top: '44px', bottom: 0, width: '2px', background: '#E8E5E1' }} />
                )}
                {/* Number circle */}
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: step.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
                  <span style={{ color: '#FFFFFF', fontSize: '0.8rem', fontWeight: '800' }}>{step.step}</span>
                </div>
                <div style={{ flex: 1, paddingTop: '8px' }}>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: BRAND.dark, marginBottom: '10px', letterSpacing: '-0.01em' }}>{step.title}</h3>
                  <p style={{ fontSize: '0.95rem', color: BRAND.muted, lineHeight: '1.65', marginBottom: '10px' }}>{step.description}</p>
                  <div style={{ background: BRAND.light, borderRadius: '8px', padding: '10px 14px', borderLeft: `3px solid ${step.color}` }}>
                    <p style={{ fontSize: '0.82rem', color: '#5A5A6E', lineHeight: '1.55', margin: 0, fontStyle: 'italic' }}>{step.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DAILY BREAKDOWN ── */}
      <section style={{ background: BRAND.light, padding: 'clamp(64px, 10vw, 120px) 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <h2 style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)', fontWeight: '800', color: BRAND.dark, letterSpacing: '-0.02em', marginBottom: '12px' }}>
              What happens every day
            </h2>
            <p style={{ fontSize: '1rem', color: BRAND.muted, lineHeight: '1.65', maxWidth: '480px', margin: '0 auto' }}>
              Three core practices across Mind, Move, and Journal, plus an optional Faith pathway that can be enabled anytime. All building toward a more stable, regulated, and intentional life.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {dailyBreakdown.map(item => (
              <div key={item.pillar} style={{ background: BRAND.white, borderRadius: '20px', overflow: 'hidden', border: '1px solid #E8E5E1', transition: 'box-shadow 0.2s, transform 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <div style={{ background: item.color, padding: '24px 28px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '1.75rem' }}>{item.icon}</span>
                    <span style={{ background: 'rgba(255,255,255,0.2)', color: BRAND.white, fontSize: '0.75rem', fontWeight: '600', padding: '3px 12px', borderRadius: '100px' }}>{item.time}</span>
                  </div>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: BRAND.white, margin: 0, letterSpacing: '-0.02em' }}>{item.pillar}</h3>
                </div>
                <div style={{ padding: '24px 28px' }}>
                  <p style={{ fontSize: '0.875rem', color: BRAND.muted, lineHeight: '1.65', marginBottom: '20px' }}>{item.what}</p>
                  <p style={{ fontSize: '0.75rem', fontWeight: '700', color: item.color, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '10px' }}>Example activities</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {item.examples.map(ex => (
                      <div key={ex} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.8rem', color: '#5A5A6E' }}>
                        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: item.color, flexShrink: 0, marginTop: '5px' }} />
                        {ex}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TWO PATHWAYS ── */}
      <section style={{ background: BRAND.white, padding: 'clamp(64px, 10vw, 120px) 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <h2 style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)', fontWeight: '800', color: BRAND.dark, letterSpacing: '-0.02em', marginBottom: '12px' }}>
              Two pathways. The same program.
            </h2>
            <p style={{ fontSize: '1rem', color: BRAND.muted, lineHeight: '1.65', maxWidth: '500px', margin: '0 auto' }}>
              You don't need a therapist to use Momentum. But if you have one, we make the integration seamless.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            {pathways.map(p => (
              <div key={p.label} style={{ background: p.lightColor, border: `2px solid ${p.color}25`, borderRadius: '20px', padding: '36px', transition: 'box-shadow 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{p.icon}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: BRAND.dark, margin: 0 }}>{p.label}</h3>
                </div>
                <p style={{ fontSize: '0.85rem', fontWeight: '600', color: p.color, marginBottom: '14px', fontStyle: 'italic' }}>{p.tagline}</p>
                <p style={{ fontSize: '0.875rem', color: BRAND.muted, lineHeight: '1.65', marginBottom: '20px' }}>{p.description}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {p.includes.map(item => (
                    <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.82rem', color: '#3A3A4A' }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ color: BRAND.white, fontSize: '0.6rem', fontWeight: '700' }}>✓</span>
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
                <div style={{ background: BRAND.white, borderRadius: '10px', padding: '12px 14px', borderLeft: `3px solid ${p.color}` }}>
                  <p style={{ fontSize: '0.8rem', color: BRAND.muted, lineHeight: '1.5', margin: 0 }}>{p.forWho}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section className="hero-marble-bg" style={{ padding: 'clamp(64px, 10vw, 100px) 24px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: '800', color: '#f1fff8', lineHeight: '1.15', letterSpacing: '-0.02em', marginBottom: '16px' }}>
            Ready to start?
          </h2>
          <p style={{ fontSize: '1.05rem', color: 'rgba(241,255,248,0.75)', lineHeight: '1.65', marginBottom: '36px' }}>
            Day 1 is waiting. So are the 167 hours between now and your next appointment.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')}
              style={{ background: '#ffd166', color: '#041b12', fontWeight: '700', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#c49460'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#D4A574'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Start My Daily Practice
            </button>
            <button onClick={() => navigate('/journey')}
              style={{ background: 'transparent', color: BRAND.white, fontWeight: '600', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.65)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'transparent'; }}>
              Explore the 365-Day Journey
            </button>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default HowItWorksPage;
